import { useEffect, useState, useRef } from "react"
import api from "../api/api";
import Loader from "../shared/loader";
import Pagination from "../shared/pagination";
import { saveAs } from 'file-saver';
import stringHelpers from "../../utilities/stringHelpers";
import arrayUtilities from "../../utilities/arrayUtilities";
import imageUtilities from "../../utilities/imageUtilities";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { toDateTimeString } from "../../utilities/dateUtilities";
import { SearchBarWithDebounce } from "../shared/searchBarWithDebounce";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import { PaymentsDataFiltersModal } from "./filters-modals/PaymentsDataFiltersModal";
import { PaymentsScheduleModal } from "../reports/schedule-modals/paymentsScheduleModal";

const DEFAULT_DATE_SEARCH_MODE = 1

export default function PaymentsData(props) {
    const [data, setData] = useState(null);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [lastPage, setLastPage] = useState(false);
    const [similarityThreshold, setSimilarityThreshold] = useState(65);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [siteId, setSiteId] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [datePreset, setDatePreset] = useState(null);
    const [dateSearchMode, setDateSearchMode] = useState(DEFAULT_DATE_SEARCH_MODE)

    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);

    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);

    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        siteId: siteId,
        dateSearchMode: dateSearchMode,
        tagIds: selectedTags,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)

    const fetchPaymentsData = (page, similarityThreshold, searchQuery, site, from, to, dateMode, tagIds) => {
        setIsLoading(true);
        api.getPaymentsData(
            data => { setData(data); setIsLoading(false) },
            () => setIsLoading(false),
            searchQuery, !stringHelpers.isNullOrEmpty(site) ? site : null, from, to, parseInt(page), parseFloat(similarityThreshold / 100), 0, dateMode, tagIds, props.organisationId, props.token
        )
    }

    const handleExport = () => {
        api.getPaymentsData(
            (blob, filename) => { saveAs(blob, filename) },
            () => setIsLoading(false),
            searchQuery, !stringHelpers.isNullOrEmpty(siteId) ? siteId : null, fromDate, toDate, parseInt(pageNumber), parseFloat(similarityThreshold / 100), 1, dateSearchMode, selectedTags, props.organisationId, props.token
        )
    }

    useEffect(() => {
        fetchPaymentsData(0, 65, "", null, null, null, DEFAULT_DATE_SEARCH_MODE, selectedTags);
        setAppliedFilters({
            "Date Mode": "Allow Overlap",
        })
        setShowFilterPills(true)
    }, []);

    useEffect(() => {
        fetchPaymentsData(pageNumber, similarityThreshold, searchQuery, siteId, fromDate, toDate, dateSearchMode, selectedTags);
    }, [pageNumber]);

    const changepageNumber = (num) => {
        setPageNumber(num);
    };
    const isLastPage = () => {
        if (data && data !== undefined) {
            setLastPage(false)
            setPageCount(Math.ceil(data.totalCount / 100));
            if (pageNumber + 1 >= Math.ceil(data.totalCount / 100)) {
                setLastPage(true)
            }
        }
    };

    const resetFilters = () => {
        setSiteId(null)
        setFromDate(null)
        setToDate(null)
        setDatePreset(null)
        setDateSearchMode(DEFAULT_DATE_SEARCH_MODE)
        setSelectedTags([])
        setAppliedFilters({
            "Date Mode": "Allow Overlap",
        })
        setShowFilterPills(true)
        setPageNumber(0);
        fetchPaymentsData(0, similarityThreshold, searchQuery, null, null, null, DEFAULT_DATE_SEARCH_MODE, []);
    }

    const changeSimilarityThresholdTimeout = useRef();
    const changeSimilarityThreshold = (newThreshold) => {

        setSimilarityThreshold(Number(newThreshold));

        if (changeSimilarityThresholdTimeout.current) {
            clearTimeout(changeSimilarityThresholdTimeout.current);
            changeSimilarityThresholdTimeout.current = null;
        }

        changeSimilarityThresholdTimeout.current = setTimeout(() => {
            fetchPaymentsData(pageNumber, newThreshold, searchQuery, siteId, fromDate, toDate, dateSearchMode, selectedTags);
        }, 1000);
    };

    const resetSimilarityThreshold = (query = searchQuery, page = pageNumber) => {
        if (changeSimilarityThresholdTimeout.current) {
            clearTimeout(changeSimilarityThresholdTimeout.current);
            changeSimilarityThresholdTimeout.current = null;
        }
        setSimilarityThreshold(65);
        fetchPaymentsData(page, 65, query, siteId, fromDate, toDate, dateSearchMode, selectedTags);
    };

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Site":
                setSiteId(null)
                fetchPaymentsData(0, similarityThreshold, searchQuery, null, fromDate, toDate, dateSearchMode, selectedTags)
                break
            case "From":
                setFromDate(null)
                fetchPaymentsData(0, similarityThreshold, searchQuery, siteId, null, toDate, dateSearchMode, selectedTags)
                break
            case "To":
                setToDate(null)
                fetchPaymentsData(0, similarityThreshold, searchQuery, siteId, fromDate, null, dateSearchMode, selectedTags)
                break
            case "Date Range":
                setFromDate(null)
                setToDate(null)
                setDatePreset(null)
                fetchPaymentsData(0, similarityThreshold, searchQuery, siteId, null, null, dateSearchMode, selectedTags)
                break
            case "Date Mode":
                setDateSearchMode(DEFAULT_DATE_SEARCH_MODE)
                fetchPaymentsData(0, similarityThreshold, searchQuery, siteId, fromDate, toDate, DEFAULT_DATE_SEARCH_MODE, selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                fetchPaymentsData(0, similarityThreshold, searchQuery, siteId, fromDate, toDate, dateSearchMode, [])
                break
            default:
                break
        }
        setPageNumber(0)
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "From" || closedLabel === "To" || closedLabel === "Date Range") {
                updatedFilters.From = null
                updatedFilters.To = null
                updatedFilters["Date Range"] = null
            } else if (closedLabel === "Date Mode") {
                updatedFilters["Date Mode"] = "Allow Overlap"
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                fetchPaymentsData(0, similarityThreshold, searchQuery, siteId, fromDate, toDate, dateSearchMode, remainingIds)
                break
            default:
                break
        }
        setPageNumber(0)
    }

    useEffect(() => isLastPage(), [data]);

    const fetchSiteUrl = (id) => {
        return `/organisation/${props.organisationId}/client/${props.sites.find(site => site.id === id)?.clientId}/site/${id}`;
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-3">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="mb-1 d-flex align-items-center">
                                Payments
                            </h5>
                            <div className="d-flex align-items-center">
                                <div className='mr-1'>
                                    <SearchBarWithDebounce
                                        onSearchQueryChange={query => {
                                            setSearchQuery(query);
                                            setPageNumber(0);
                                            if (stringHelpers.isNullOrEmpty(query)) {
                                                resetSimilarityThreshold(query, 0)
                                            }
                                            else {
                                                fetchPaymentsData(0, similarityThreshold, query, siteId, fromDate, toDate, dateSearchMode, selectedTags)
                                            }
                                        }}
                                    />
                                </div>
                                <div className='d-flex align-items-center bg-white mr-3'>
                                    <div className='d-flex align-items-center px-2' style={ { height: "34px" } }>
                                        <label className='m-0 mr-3 text-nowrap'>Similarity: { similarityThreshold }%</label>
                                        <input
                                            class="form-range"
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={ similarityThreshold }
                                            onChange={ (e) => changeSimilarityThreshold(e.target.value) }
                                        />
                                    </div>
                                </div>
                                <div className='mr-2'>
                                    <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                </div>
                                <div className="d-flex align-items-center justify-content-end ml-auto">
                                    <button
                                        className="btn btn-primary btn-sm mr-1 ml-1 nowrap"
                                        onClick={ () => fetchPaymentsData(pageNumber, similarityThreshold, searchQuery, siteId, fromDate, toDate, dateSearchMode, selectedTags) }>
                                        <i className="fas fa-redo"></i> Refresh
                                    </button>
                                    <button
                                        className="btn btn-dark btn-sm mr-1 ml-1 nowrap"
                                        onClick={ () => handleExport() }>
                                        <i className="fas fa-download"></i> Export
                                    </button>
                                    <button
                                        className="btn btn-secondary btn-sm mr-1 ml-1 nowrap"
                                        onClick={ () => setShowScheduleExportModal(true) }>
                                        <i className="far fa-clock"></i> Schedule Report
                                    </button>
                                </div>
                            </div>
                        </div>
                        {
                            showFilterPills && appliedFilters &&
                            <div className="px-3 py-2 border-top d-flex align-items-center flex-wrap">
                                {
                                    Object.entries(appliedFilters).map(
                                        ([label, value]) => {
                                            const resettable = label === "Date Mode"
                                            return <AppliedFilterPill
                                                key={ label }
                                                label={ label }
                                                value={ value }
                                                isResettable={ resettable }
                                                renderItemLabel={ renderTagFilterItemLabel }
                                                onTextClick={ () => setShowFiltersModal(true) }
                                                onCloseClick={ handleFilterPillClose }
                                                onRemoveItem={ handleFilterPillItemRemove }
                                            />
                                        }
                                    )
                                }
                                {appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={() => resetFilters()}>Reset all filters</button>}
                            </div>
                        }
                    </div>

                    { isLoading
                        ? <Loader />
                        : <>
                            {
                                data && !arrayUtilities.isNullOrEmpty(data.items) &&
                                (() => {
                                    const hasSimilarity = data.items.some(item => item.similarity > 0);
                                    return (
                                <div className="card-body bg-white border-top details-container p-0 overflow-auto">
                                    <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                        <thead className="bg-200 text-900">
                                            <tr>
                                                <th>&nbsp;</th>
                                                <th>VRM</th>
                                                { hasSimilarity && <th>Similarity</th> }
                                                <th>Site Name</th>
                                                <th>Location</th>
                                                <th>Start</th>
                                                <th>End</th>
                                                <th className="text-right pr-4">Amount</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {
                                                data.items.map((item, index) =>
                                                    <tr key={ index }>
                                                        <td><div className="logo-container">{ imageUtilities.fetchPaymentProviderLogo(item.provider) }</div></td>
                                                        <td><Link className="number-plate-link"><h6 className="mb-0 small-number-plate">{ item.vrm }</h6></Link></td>
                                                        {
                                                            hasSimilarity &&
                                                            <td>
                                                                <span class="progress" style={ { minWidth: "100px" } }>
                                                                    <span
                                                                        class={ `progress-bar text-left pl-1 ${ item.similarity > 0.99 ? "bg-success" : (item.similarity > 0.6 ? "bg-warning" : "bg-danger") }` }
                                                                        role="progressbar"
                                                                        style={ { width: `${ item.similarity * 100 }%` } }
                                                                        aria-valuenow={ item.similarity }
                                                                        aria-valuemin="0"
                                                                        aria-valuemax="100"
                                                                    >{ stringHelpers.formatDecimal((item.similarity * 100), 0) }%
                                                                    </span>
                                                                </span>
                                                            </td>
                                                        }
                                                        <td>
                                                            <Link to={ fetchSiteUrl(item.siteId) }><h6 style={ { color: "#5e6e82", margin: "0" } }>{ item.siteName }</h6></Link>
                                                        </td>
                                                        <td>{ item.location }</td>
                                                        <td>{ toDateTimeString(item.start) }</td>
                                                        <td>{ toDateTimeString(item.end) }</td>
                                                        <td className="text-right pr-4">{ stringHelpers.formatDecimal(item.amount, 2, "£") }</td>
                                                    </tr>)
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                    )
                                })()
                            }
                        </>
                    }
                    <div className="d-flex justify-content-center row align-items-center pt-2">
                        <div className="col-auto align-items-center">
                            <Pagination totalResults={ data?.totalCount } currentPage={ pageNumber } changePageNumber={ changepageNumber } />
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <PaymentsDataFiltersModal
                    showModal={showFiltersModal}
                    handleClose={() => setShowFiltersModal(false)}
                    handleConfirm={(draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setDateSearchMode(draftFilters.dateSearchMode)
                        setSiteId(draftFilters.siteId)
                        setSelectedTags(draftFilters.selectedTags)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        setPageNumber(0)
                        fetchPaymentsData(0, similarityThreshold, searchQuery, draftFilters.siteId, draftFilters.fromDate, draftFilters.toDate, draftFilters.dateSearchMode, draftFilters.selectedTags)
                    }}
                    initialFromDate={fromDate}
                    initialToDate={toDate}
                    initialDatePreset={datePreset}
                    initialDateSearchMode={dateSearchMode}
                    initialSiteId={siteId}
                    initialSelectedTags={selectedTags}
                    initialAppliedFilters={appliedFilters}
                    sites={props.sites}
                    tags={props.tags || []}
                    key="payments-data-filters-modal"
                />
            }
            {
                showScheduleExportModal &&
                <PaymentsScheduleModal
                    sites={ props.sites }
                    tags={ props.tags || [] }
                    organisationId={ props.organisationId }
                    token={ props.token }
                    initialParameters={ scheduleInitialParameters }
                    onCancel={ closeScheduleExportModal }
                    onSaved={ closeScheduleExportModal }
                />
            }
        </div>
    )
}
